import { useState, useCallback } from "react";
import { Upload } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "react-i18next";

interface JobDescriptionUploadDropzoneProps {
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
  progress?: number;
  progressStatus?: string;
  isFileUploadEnabled?: boolean; // New parameter to control file upload behavior
}

export const JobDescriptionUploadDropzone = ({ 
  onFileSelect, 
  isLoading = false, 
  progress = 0, 
  progressStatus = "", 
  isFileUploadEnabled = true 
}: JobDescriptionUploadDropzoneProps) => {
  
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      setUploadedFileName(file.name);
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const handleClick = () => {
    if (isFileUploadEnabled) {
      const fileInput = document.getElementById('job-description-upload');
      if (fileInput && !isLoading) {
        fileInput.click();
      }
    }
  };

  return (
    <div 
      className={`h-full w-full flex flex-col items-center justify-center p-8 transition-colors ${
        isDragging ? 'bg-blue-50' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      {isLoading ? (
        <div className="w-full max-w-md">
          <h3 className="text-lg font-medium mb-4">{t("jobs.uploadModal.dragAndDrop")}</h3>
          <Progress value={progress} className="h-2 mb-2" />
          <p className="text-sm text-gray-500">{progressStatus || t("jobs.uploadModal.pleaseWait")}</p>
        </div>
      ) : uploadedFileName ? (
        <div className="flex flex-col items-center text-center">
          <h3 className="text-lg font-medium text-green-600">{uploadedFileName}</h3>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center">
          <div className="w-24 h-24 flex items-center justify-center text-blue-800 mb-6">
            <Upload size={56} strokeWidth={1.25} />
          </div>
          <h3 className="text-xl font-semibold mb-2">{t("jobs.uploadModal.dragAndDrop")}</h3>
          <p className="text-gray-500 text-sm mb-1">
            {t("jobs.uploadModal.uploadDesc")}
          </p>
          <p className="text-gray-500 text-sm">
            {t("jobs.uploadModal.formats")}
          </p>
          <input 
            id="job-description-upload"
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                const file = e.target.files[0];
                setUploadedFileName(file.name);
                onFileSelect(file);
                e.target.value = ''; // Reset file input
              }
            }}
            className="hidden"
            disabled={isLoading}
          />
        </div>
      )}
    </div>
  );
};
